import type { Preferences } from "./storage";

type SoundName =
  | "shoot"
  | "hit"
  | "clog"
  | "escape"
  | "shield"
  | "power"
  | "milestone"
  | "victory"
  | "click";

export type MusicMood = "classic" | "cruising" | "dark" | "showdown";

export interface MusicProfile {
  readonly mood: MusicMood;
  readonly melody: readonly number[];
  readonly bass: readonly number[];
  readonly interval: number;
  readonly duration: number;
  readonly volume: number;
}

const MUSIC_PROFILES: Record<MusicMood, MusicProfile> = {
  classic: {
    mood: "classic",
    melody: [196, 247, 294, 247, 220, 277, 330, 277],
    bass: [98, 110],
    interval: 390,
    duration: 0.29,
    volume: 0.072,
  },
  cruising: {
    mood: "cruising",
    melody: [220, 277, 330, 277, 247, 294, 370, 294],
    bass: [110, 123],
    interval: 360,
    duration: 0.28,
    volume: 0.074,
  },
  dark: {
    mood: "dark",
    melody: [196, 233, 294, 233, 175, 220, 277, 220],
    bass: [87, 98, 82, 98],
    interval: 330,
    duration: 0.27,
    volume: 0.076,
  },
  showdown: {
    mood: "showdown",
    melody: [196, 247, 294, 370, 220, 277, 330, 415],
    bass: [98, 110, 123, 110],
    interval: 285,
    duration: 0.24,
    volume: 0.08,
  },
};

export function musicProfileForLevel(levelId: number | null): MusicProfile {
  if (levelId === 10) return MUSIC_PROFILES.showdown;
  if (levelId !== null && levelId >= 7) return MUSIC_PROFILES.dark;
  if (levelId !== null && levelId >= 4) return MUSIC_PROFILES.cruising;
  return MUSIC_PROFILES.classic;
}

const NOTES: Record<SoundName, readonly number[]> = {
  shoot: [220],
  hit: [340],
  clog: [420, 620, 820],
  escape: [260, 180, 110],
  shield: [700, 900],
  power: [440, 660, 880],
  milestone: [523, 659, 784, 1047],
  victory: [392, 523, 659, 784, 1047],
  click: [520],
};

export class AudioDirector {
  private context: AudioContext | null = null;
  private musicTimer: ReturnType<typeof globalThis.setInterval> | null = null;
  private musicRequest = 0;
  private musicLevelId: number | null = null;
  private activeMusicMood: MusicMood | null = null;
  private preferences: Preferences;

  constructor(preferences: Preferences) {
    this.preferences = preferences;
  }

  setPreferences(preferences: Preferences): void {
    this.preferences = preferences;
    if (preferences.muteAll || !preferences.music) this.stopMusic();
  }

  async unlock(): Promise<void> {
    this.context ??= new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
  }

  effect(name: SoundName): void {
    if (this.preferences.muteAll || !this.preferences.effects) return;
    void this.unlock()
      .then(() => {
        if (this.preferences.muteAll || !this.preferences.effects) return;
        this.sequence(NOTES[name], name === "shoot" ? 0.025 : 0.06);
      })
      .catch(() => {
        // Audio is optional; gameplay remains complete if a browser blocks it.
      });
  }

  startMusic(levelId: number | null = this.musicLevelId): void {
    const nextMood = musicProfileForLevel(levelId).mood;
    if (this.musicTimer !== null && this.activeMusicMood !== nextMood)
      this.stopMusic();
    this.musicLevelId = levelId;
    if (
      this.preferences.muteAll ||
      !this.preferences.music ||
      this.musicTimer !== null
    )
      return;
    const request = ++this.musicRequest;
    void this.unlock()
      .then(() => {
        if (
          request !== this.musicRequest ||
          this.preferences.muteAll ||
          !this.preferences.music ||
          this.musicTimer !== null
        )
          return;
        const profile = musicProfileForLevel(this.musicLevelId);
        this.activeMusicMood = profile.mood;
        let step = 0;
        const playStep = (): void => {
          if (this.preferences.muteAll || !this.preferences.music) return;
          this.tone(
            profile.melody[step % profile.melody.length] ?? 220,
            profile.duration,
            profile.volume,
            "triangle",
          );
          if (step % 2 === 0) {
            this.tone(
              profile.bass[Math.floor(step / 2) % profile.bass.length] ?? 98,
              Math.min(0.42, profile.duration + 0.1),
              profile.mood === "classic" ? 0.019 : 0.022,
              "sine",
            );
          }
          if (
            (profile.mood === "dark" || profile.mood === "showdown") &&
            step % 4 === 3
          ) {
            this.tone(
              (profile.melody[step % profile.melody.length] ?? 220) * 2,
              0.12,
              0.008,
              "sine",
            );
          }
          step += 1;
        };
        playStep();
        this.musicTimer = globalThis.setInterval(() => {
          void this.unlock()
            .then(() => {
              playStep();
            })
            .catch(() => {
              // A suspended/blocked context simply leaves music silent.
            });
        }, profile.interval);
      })
      .catch(() => {
        // Audio is optional; gameplay remains complete if a browser blocks it.
      });
  }

  stopMusic(): void {
    this.musicRequest += 1;
    if (this.musicTimer !== null) globalThis.clearInterval(this.musicTimer);
    this.musicTimer = null;
    this.activeMusicMood = null;
  }

  haptic(pattern: number | number[]): void {
    if (
      !this.preferences.muteAll &&
      this.preferences.haptics &&
      navigator.vibrate
    )
      navigator.vibrate(pattern);
  }

  private sequence(notes: readonly number[], duration: number): void {
    notes.forEach((note, index) =>
      globalThis.setTimeout(
        () => this.tone(note, duration, 0.045, "sine"),
        index * 70,
      ),
    );
  }

  private tone(
    frequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
  ): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      this.context.currentTime + duration,
    );
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration);
  }
}
